import Empty from "@/components/Empty";
import LoadMore from "@/components/LoadMore";
import RpIcon from "@/components/RpIcon";
import TransItem from "@/components/skeleton/TransItem";
import API from "@/constant/API";
import RouteConstant from "@/constant/RouteConstant";
import useLoadMore from "@/hook/useLoadMore";
import useRenders from "@/hook/useRenders";
import SearchItem from "@/pages/components/SearchItem";
import axiosInstance from "@/utils/axios";
import { Box, Stack } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import CardTransItem from "./CardTransItem";
import SimpleHeader from "@/pages/components/Headers/SimpleHeader";
import useScrollList from "@/hook/useScrollList";

export interface CardTransItemType {
  transactionId: string,
  refundTransactionAmount: null | string,
  cardId: string,
  billAmount: string,
  billCurrency: number,
  oriBillAmount: string,
  clearAmount: string,
  clearTime: number,
  reversalTotalBillAmount: null | string,
  reversalBillCurrency: null,
  reversalLastTime: null,
  refundAmount: null,
  refundTime: null,
  transactionAmount: string,
  transactionCurrency: number,
  conversionRate: string,
  channel: string,
  fxFees: string,
  atmFees: string,
  wallet?: null,
  clearedAt?: null,
  status: string,
  isRefund: number,
  hasRefund: number,
  mccCode: number,
  merchantId: string,
  merchantName: string,
  merchantCity: string,
  merchantPostCode?: null | number,
  merchantState: null | number,
  merchantCountry: string,
  mccCategory: "Computer Network Services",
  responseCode?: null,
  declineReason?: null,
  label?: null,
  transactionDateTime?: null,
  createTime: number,
  billCurrencyCode: string,
  transactionCurrencyCode: string,
  mcc: number,
  last4: number,
  refundStatus?: null,
  cardAuthorizations: string[],
  refundOrders: any[],
  payVersion: number
}

// const mockData = [
//   {
//     transactionId: "tid_6NBJHS4STV", 
//     refundTransactionAmount: null, 
//     cardId: "28beab6d-fc81-47d4-8d7d-836298ed1ecf", 
//     billAmount: 10.00, 
//     billCurrency: 840, 
//     oriBillAmount: 10.00, 
//     clearAmount: 10.00, 
//     clearTime: 1702350812000, 
//     reversalTotalBillAmount: null, 
//     reversalBillCurrency: null, 
//     reversalLastTime: null, 
//     refundAmount: null, 
//     refundTime: null, 
//     transactionAmount: 74.00, 
//     transactionCurrency: 156,
//     conversionRate: "1.1428571428571428571", 
//     channel: "POS", 
//     fxFees: 1.00, 
//     atmFees: 0.00, 
//     wallet: null, 
//     clearedAt: null, 
//     status: "CLEARED", 
//     isRefund: 0, 
//     hasRefund: 0, 
//     mccCode: 4816, 
//     merchantId: "121635420100000", 
//     merchantName: "wechatpay", 
//     merchantCity: "Shenzhen", 
//     merchantPostCode: null, 
//     merchantState: null, 
//     merchantCountry: "CHN", 
//     mccCategory: "Computer Network Services", 
//     responseCode: null, 
//     declineReason: null, 
//     label: null, 
//     transactionDateTime: null,
//      createTime: 1702350797000, 
//      billCurrencyCode: "USD", 
//      transactionCurrencyCode: "CNY", 
//      mcc: 4816, 
//      last4: 4928, 
//      refundStatus: null, 
//      cardAuthorizations: [], 
//      refundOrders: [], 
//      payVersion: 1
//     }

// ];

export default function CardTransactions({cardId, pageable, searchAll = false, cacheScrollList = false,
  pageTitle = false, hideTitle = false, disableScrollControl = false, searchParams, cardInfo}) {
  const [data, setData] = useState([]);
  const [initialLoading, setInitialLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const {t} = useTranslation();
  const navigate = useNavigate();

  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(pageable ? 20 : 10);
  const [hasMore, setHasMore] = useState(false);
  const [search, setSearch] = useState(false);
  const [searchValues, setSearchValues] = useState(searchParams ?? {});

  const hasMoreRef = useRef(false);
  const pageNumRef = useRef(1);

  const _restoreList = (listInfo) => {
    setData(listInfo.list);
    setPageNum(listInfo.pageNum);
    setPageSize(listInfo.pageSize);
  }

  const {
    restoreScrollPosition,
    cacheScrollList: doCacheScrollList,
    clearScrollList,
  } = useScrollList({
    page: RouteConstant.CARD_TRANS_LIST,
    restoreList: _restoreList,
  });

  const _cacheScrollList = () => {
    if (!cacheScrollList) return;

    doCacheScrollList({
      list: data,
      pageNum: pageNum,
      pageSize: pageSize,
    })
  }

  useEffect(() => {
    if (cacheScrollList) {
      restoreScrollPosition();
    }
  }, [cacheScrollList]);

  useEffect(() => {
    if (pageable) {
      // _onRefresh();
    }
  }, [pageable])

  useEffect(() => {
    if (searchAll) {
      // _onRefresh();
    }
  }, [searchAll]);

  useEffect(() => {
    // _onRefresh();
  }, [cardId, searchValues]);

  useEffect(() => {
    pageNumRef.current = pageNum;
    _loadData();
  }, [pageNum, pageSize, cardId, searchValues]);

  const _onRefresh = () => {
    // history clean up
    setData([]);
    if (pageNum != 1) {
      setPageNum(1);
    } else {
      _loadData();
    }
  }

  const _nextPage = () => {
    if (hasMoreRef.current) {
      setPageNum(pageNumRef.current + 1);
    }
  }

  const _loadData = async () => {
    if (initialLoading || loading) return;

    if (pageNum == 1) {
      setInitialLoading(true);
    } else {
      setLoading(true);
    }
    try {
      const res = await axiosInstance.post(API.CARD_TRANS_LIST, {
        pageNum, pageSize,
        cardId: cardId,
        ...searchValues,
      });
      if (pageNum == 1) {
        setData(res.list || []);
        // setData(mockData)
      } else {
        setData([...data, ...(res.list ?? [])]);
      }

      const hasMore = res.total >= pageNum * pageSize;
      setHasMore(hasMore);
      hasMoreRef.current = hasMore;
    } finally {
      setInitialLoading(false);
      setLoading(false);
    }
  }

  useLoadMore({
    enable: !!pageable,
    loadMore: () => {
      if (disableScrollControl) return;
      _nextPage();
    },
  });
  const {renderDate} = useRenders();

  const handleSearch = (values) => {
    if (values.daysLasting === 'customize') {
      const startTime = renderDate(values.startTime)
      const endTime = renderDate(values.endTime)
      delete values.daysLasting;
      values = {...values, startTime, endTime};
    } else if (values.daysLasting === 'all') {
      values = {};
    } else {
      delete values.startTime;
      delete values.endTime;
    }
    setSearchValues(values);
    setSearch(false)
  }

  const _toTransList = () => {
    if (pageable) {
      setSearch(true)
      return;
    }
    navigate(`${RouteConstant.CARD_TRANS_LIST}?cardId=${cardId}&cardType=${cardInfo.cardType}`);
  }

  const _onBackClick = () => {
    navigate(-1);
    clearScrollList();
  }

  if (!searchAll && !cardId) return null;

  return (
    <Box>
       {/* action={
        <RpIcon icon={pageable ? 'filter' : 'arrow-right-simple'} size={20} clickable onClick={_toTransList}/>
      } */}
      {pageTitle && <SimpleHeader onBackClick={_onBackClick} title={t("transactions")} />}

      <div style={{ width: '100%', padding: pageTitle ? '0 16px' : '' }}>
        {(pageTitle || hideTitle) ? null : <Stack direction={'row'} gap={'4px'} sx={{marginBottom:'10px'}} justifyContent={'space-between'}>
          <span className={'h4'}>{t("transactions")}</span>
          <Stack direction={'row'} alignItems={'center'} className={'small'} onClick={_toTransList}>
            <span>{t('more')}</span>
            <RpIcon icon={pageable ? 'filter' : 'arrow-right-simple'} size={16} clickable/>
          </Stack>
        </Stack>}

        <div className={`flex-1 ${pageTitle || hideTitle ? '' : 'card-box'}`}>
          {initialLoading ? (
            // <Loading size="md" />
            <>
              <TransItem/>
              <TransItem/>
              <TransItem/>
            </>
          ) : data.length === 0 ? (
            <div>
              <Empty text={t('empty_data')}/>
            </div>
          ) : (
            <div>
              {data.map((item, idx) => (
                <div key={idx} className="cursor-pointer border-b border-solid border-b-border" onClick={_cacheScrollList}>
                  <CardTransItem record={item} cardType={cardInfo?.cardType} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {pageable && data.length > 0 && <LoadMore loading={loading} hasMore={hasMore} onClick={_nextPage} />}

      <SearchItem open={search} onClose={() => setSearch(false)} onSearch={handleSearch}></SearchItem>
    </Box>
  );
}
